import { Box, Divider, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { J2Subtract } from '@e3dc-react/icons'
import { MultiSelect } from '@e3dc-react/shell/Elements/Select'
import { RemoveIconButton } from '@e3dc-react/shell/Elements/Buttons'
import { ChartType, CustomTrace, CustomTraceWithSum, SumKeyOperand } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { TraceDetails } from 'src/Hooks/use-chart-traces'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { isUndefined } from 'lodash'
import { TraceKey } from 'src/Libs/system-charts/shared/config/traceConf'
import LegendItem from '../../shared/components/LegendItem'

interface Props {
  chartType: ChartType
  trace: CustomTraceWithSum
  updateTraceWithSum: ({ traceAlternateKey, keys, operand }: { traceAlternateKey: string; keys: string[]; operand: SumKeyOperand }) => void
  removeTraceWithSum: (trace: CustomTrace) => void
  availableTraceNamesWithDetails: TraceDetails[]
  handlePopoverOpen: (traceKey: TraceKey, event?: React.MouseEvent<Element, MouseEvent>) => void
}

const SumItem: React.FC<Props> = ({
  chartType,
  trace,
  updateTraceWithSum,
  handlePopoverOpen,
  availableTraceNamesWithDetails,
  removeTraceWithSum,
}) => {
  const sumKeysWithAddOperand = useMemo(() => trace.sumKeys.filter(sumKey => sumKey.operand === 'add').map(sumKey => sumKey.key), [trace])
  const sumKeysWithSubtractOperand = useMemo(() => trace.sumKeys.filter(sumKey => sumKey.operand === 'subtract').map(sumKey => sumKey.key), [trace])
  const { t } = useCustomTranslation()
  const resultExample = useMemo(() => {
    const result: string[] = []
    const mappedSumKeysWithAddOperand = sumKeysWithAddOperand
      .map(key => availableTraceNamesWithDetails.find(traceName => traceName.key === key)?.name)
      .filter(name => !isUndefined(name))

    const mappedSumKeysWithSubtractOperand = sumKeysWithSubtractOperand
      .map(key => availableTraceNamesWithDetails.find(traceName => traceName.key === key)?.name)
      .filter(name => !isUndefined(name))

    if (mappedSumKeysWithAddOperand.length > 2) {
      result.push(mappedSumKeysWithAddOperand.slice(0, 2).join(' + ') + ' + ...')
    } else if (mappedSumKeysWithAddOperand.length >= 1) {
      result.push(mappedSumKeysWithAddOperand.join(' + '))
    }

    if (mappedSumKeysWithSubtractOperand.length > 2) {
      result.push(' - ' + mappedSumKeysWithSubtractOperand.slice(0, 2).join(' - ') + ' - ...')
    } else if (mappedSumKeysWithSubtractOperand.length >= 1) {
      result.push(' - ' + mappedSumKeysWithSubtractOperand.join(' - '))
    }

    return result.join()
  }, [availableTraceNamesWithDetails, sumKeysWithAddOperand, sumKeysWithSubtractOperand])

  const traceKey = trace.alternateKey ?? trace.key
  const legendIconType = traceKey === 'ASOC' ? 'gradient' : chartType === 'bar' ? 'bar' : 'line'

  return (
    <>
      <Box
        key={trace.alternateKey}
        sx={theme => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: theme.spacing(2) })}
      >
        <LegendItem
          type={legendIconType}
          key={trace.alternateKey}
          color={trace.color}
          title={trace.name ?? ''}
          onClick={e => handlePopoverOpen(traceKey as TraceKey, e)}
        />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MultiSelect
            options={availableTraceNamesWithDetails.map(key => ({ value: key.key, label: key.name }))}
            value={sumKeysWithAddOperand}
            onChange={value => {
              updateTraceWithSum({ traceAlternateKey: trace.alternateKey, keys: value ?? [], operand: 'add' })
            }}
            label={t('systemDataHandler.editor.selectToAddLabel')}
          />
          <J2Subtract />
          <MultiSelect
            options={availableTraceNamesWithDetails.map(key => ({ value: key.key, label: key.name }))}
            value={sumKeysWithSubtractOperand}
            onChange={value => {
              updateTraceWithSum({ traceAlternateKey: trace.alternateKey, keys: value ?? [], operand: 'subtract' })
            }}
            label={t('systemDataHandler.editor.selectToSubtractLabel')}
          />
        </Box>
        {<RemoveIconButton onClick={() => removeTraceWithSum(trace)} />}
        <Divider />
      </Box>
      <Typography variant="subtitle2" sx={theme => ({ paddingBottom: theme.spacing(2) })}>
        {t('systemDataHandler.editor.customTraceSumResult')} {resultExample}
      </Typography>
    </>
  )
}

export default SumItem
